import { useEffect } from "react";

const setMeta = (selector, attrName, attrValue, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * useSEO — title + meta description, optionally OG tags + canonical + JSON-LD.
 *
 * Backward-compatible: useSEO(title, description) still works.
 * Full form: useSEO(title, description, { canonical, ogImage, ogType, jsonLd, suffix })
 *   - suffix:  string to append to the title (default " · Instabridge Funding").
 *              Pass `false` (or empty string) to disable for fully-custom titles.
 */
export const useSEO = (title, description, opts = {}) => {
  const { canonical, ogImage, ogType = "website", jsonLd, suffix, noindex = false } = opts;

  useEffect(() => {
    if (title) {
      const finalTitle = suffix === false || suffix === "" ? title : `${title}${suffix || " · Instabridge Funding"}`;
      document.title = finalTitle;
      setMeta('meta[property="og:title"]', "property", "og:title", finalTitle);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", finalTitle);
    }
    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    setMeta('meta[property="og:type"]', "property", "og:type", ogType);
    if (ogImage) {
      setMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
      setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    }
    if (canonical) {
      setLink("canonical", canonical);
      setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    }
    if (noindex) {
      setMeta('meta[name="robots"]', "name", "robots", "noindex");
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }

    // JSON-LD structured data — managed in one dedicated <script id="page-jsonld">
    let scriptEl = document.head.querySelector('script[type="application/ld+json"]#page-jsonld');
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.type = "application/ld+json";
        scriptEl.id = "page-jsonld";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }
  }, [title, description, canonical, ogImage, ogType, jsonLd, suffix, noindex]);
};
